import React, { useEffect, useState } from 'react';
import OnboardingHeader from '@/shared/components/navigation/OnboardingHeader';
import Button from '@/shared/components/Button';
import CreateProfile from './screens/CreateProfile';
import { useAuth } from '@/services/auth/AuthProvider';
import Progress from './components/Progress';
import {
  BioData,
  Expertise,
  GalleryImages,
  ProfileFormData,
  StylingPreference,
} from '@/shared/types/stylist';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useMutation, ApolloError } from '@apollo/client';
import {
  CREATE_USER,
  UPDATE_STYLIST,
  UPDATE_STYLIST_STRIPE_SETUP,
} from '@/shared/mutations';
import {
  MutationCreateUserArgs,
  MutationResult,
  MutationUpdateStylistArgs,
  MutationUpdateStylistStripeSetupArgs,
  UserType,
} from '@/shared/types/generated';
import CloudinaryService from '@/services/cloudinary/Cloudinary.service';
import SelectExpertise from './screens/SelectExpertise';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import useDimensions from '@/shared/hooks/useDimensions';
import Socials from './screens/Socials';
import SelectPreferences from './screens/SelectPreferences';
import { ButtonSelectItem } from './components/SelectButtons';
import Showcase from './screens/Showcase';
import StylistAddPayment from './screens/StylistAddPayment';
import { GET_USER } from '@/shared/queries';
import {
  BIO_MAX_LENGTH,
  HANDLE_MAX_LENGTH,
  IS_WEB,
  NAME_MAX_LENGTH,
} from '@/shared/constants/utility';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { getCurrentTimeZone, shouldSurfaceGraphQLError } from '@/shared/utils';
import {
  stylistOnboardingTitleTextOptions,
  stylistOnboardingDescriptionTextOptions,
  stylistOnboardingRequiredSteps,
  SOCIALS_STEP,
  STYLING_PREFERENCES_STEP,
  STYLING_SPECIALTY_STEP,
  CREATE_PROFILE_STEP,
  SHOWCASE_STEP,
  PAYMENT_STEP,
  READY_STEP,
} from '../../components/onboarding/onboardingContent';
import {
  OnboardingBottomContainer,
  OnboardingButtonContainer,
  OnboardingContainerStyles,
  OnboardingContentContainer,
  OnboardingFormArea,
} from '../../components/onboarding/styledComponents';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { useApollo } from '@/services/apollo/ApolloProvider';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import ScrollableView from '@/shared/components/ScrollableView';
import { green, sand, white } from '@/shared/styles/colors';
import StylistOnboardingComplete from './components/StylistOnboardingComplete';
import MissingStyleSupportText from './components/MissingStyleSupportText';

const Onboarding = () => {
  const { setSnackbarState } = useStore();
  const { auth0User, getAccessToken } = useAuth();
  const { logOut } = useApollo();
  const { width } = useDimensions();
  const isDesktop = useIsDesktop();
  const { user } = useGetUser();

  const scrollToTop = useScrollToTop();

  const [createUser] = useMutation<
    MutationResult<'createUser'>,
    MutationCreateUserArgs
  >(CREATE_USER);

  const [updateStylist] = useMutation<
    MutationResult<'updateStylist'>,
    MutationUpdateStylistArgs
  >(UPDATE_STYLIST, {
    refetchQueries: [{ query: GET_USER }],
  });

  const [updateStylistStripeSetup, { data: updateStylistStripeSetupData }] =
    useMutation<
      MutationResult<'updateStylistStripeSetup'>,
      MutationUpdateStylistStripeSetupArgs
    >(UPDATE_STYLIST_STRIPE_SETUP, { refetchQueries: [{ query: GET_USER }] });

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isRouteToStepComplete, setIsRouteToStepComplete] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const isLastStep = progress === READY_STEP;
  const isPaymentStep = progress === PAYMENT_STEP;

  const [profileFormData, setProfileFormData] = useState<ProfileFormData>({
    profileImage: '',
    firstName: '',
    lastName: '',
    gender: null,
    birthDate: undefined,
    phoneNumber: '',
    isCheckBoxChecked: false,
  });
  const [bioData, setBioData] = useState<BioData>({
    bio: '',
    tikTok: '',
    instagram: '',
    substack: '',
  });
  const [selectedExpertise, setSelectedExpertise] = useState<
    ButtonSelectItem[]
  >([]);
  const [selectedStylingPreference, setSelectedStylingPreference] = useState<
    ButtonSelectItem[]
  >([]);

  const [galleryImages, setGalleryImages] = useState<GalleryImages>({
    landscapeImage: null,
    portraitImages: [],
  });

  const [isStripeAccountSetup, setIsStripeAccountSetup] = useState(false);

  useEffect(() => {
    if (isRouteToStepComplete) return;

    if (user?.stylist && !user.stylist.bio) {
      setProgress(SOCIALS_STEP);
      setIsRouteToStepComplete(true);
    }

    if (user?.stylist && user.stylist.bio && !user.stylist.aesthetics.length) {
      setProgress(STYLING_PREFERENCES_STEP);
      setIsRouteToStepComplete(true);
    }

    if (
      user?.stylist &&
      user.stylist.bio &&
      user.stylist.aesthetics.length &&
      !user.stylist.expertise.length
    ) {
      setProgress(STYLING_SPECIALTY_STEP);
      setIsRouteToStepComplete(true);
    }

    if (
      user?.stylist &&
      user.stylist.bio &&
      user.stylist.aesthetics.length &&
      user.stylist.expertise.length &&
      !user.stylist.hasCompletedStripeSetup
    ) {
      setProgress(PAYMENT_STEP);
      setIsRouteToStepComplete(true);
    }
  }, [isRouteToStepComplete, user]);

  const nameDisableCondition =
    profileFormData.firstName.length > NAME_MAX_LENGTH ||
    profileFormData.lastName.length > NAME_MAX_LENGTH;

  const stepOneDisableCondition =
    !profileFormData.firstName ||
    !profileFormData.lastName ||
    !profileFormData.gender ||
    !profileFormData.birthDate ||
    !profileFormData.profileImage ||
    nameDisableCondition ||
    !profileFormData.isCheckBoxChecked ||
    !profileFormData.signNowDocumentId;

  const stepTwoDisableCondition =
    bioData.bio.length > BIO_MAX_LENGTH ||
    bioData.tikTok.length > HANDLE_MAX_LENGTH ||
    bioData.instagram.length > HANDLE_MAX_LENGTH ||
    bioData.substack.length > HANDLE_MAX_LENGTH;

  const getDisabledState = () => {
    switch (progress) {
      case CREATE_PROFILE_STEP: {
        return stepOneDisableCondition;
      }
      case SOCIALS_STEP: {
        return !bioData.bio || stepTwoDisableCondition;
      }
      case STYLING_SPECIALTY_STEP: {
        return selectedExpertise.length === 0;
      }
      case STYLING_PREFERENCES_STEP: {
        return selectedStylingPreference.length === 0;
      }
      case SHOWCASE_STEP: {
        return (
          !galleryImages.landscapeImage &&
          galleryImages.portraitImages.length === 0
        );
      }
      case PAYMENT_STEP: {
        return !isStripeAccountSetup && !updateStylistStripeSetupData;
      }
      default: {
        return true;
      }
    }
  };

  const getProfilePhotoUrl = async () => {
    try {
      const token = await getAccessToken();
      const result = await CloudinaryService.uploadToCloudinary(
        token,
        profileFormData.profileImage,
      );

      return result.secure_url;
    } catch (error) {
      setSnackbarState({
        open: true,
        message: 'Failed to upload profile photo. Please try again.',
        severity: 'error',
      });
      SentryService.captureError('Error uploading profile photo', error);
      throw error;
    }
  };

  const createNewUser = async () => {
    if (!auth0User?.sub || !auth0User?.email) {
      throw new Error('Missing Auth0 user data');
    }
    const profilePicUrl = await getProfilePhotoUrl();

    const phoneNumber = Boolean(profileFormData.phoneNumber)
      ? `+1${profileFormData.phoneNumber}`
      : null;

    await createUser({
      variables: {
        input: {
          auth0Id: auth0User.sub,
          email: auth0User.email,
          firstName: profileFormData.firstName,
          lastName: profileFormData.lastName,
          gender: profileFormData.gender!,
          birthDate: profileFormData.birthDate,
          timeZone: getCurrentTimeZone(),
          profilePicUrl,
          phoneNumber,
          type: UserType.Stylist,
          signNowDocumentId: profileFormData.signNowDocumentId,
        },
      },
    });
  };

  const saveGalleryImages = async () => {
    try {
      const token = await getAccessToken();

      // Upload landscape image
      let landscapeImageUrl: string | null = null;
      if (galleryImages.landscapeImage) {
        const result = await CloudinaryService.uploadToCloudinary(
          token,
          galleryImages.landscapeImage,
        );
        landscapeImageUrl = result.secure_url;
      }

      // Upload portrait images
      const portraitImageUrls: string[] = [];
      for (const image of galleryImages.portraitImages) {
        if (image) {
          const result = await CloudinaryService.uploadToCloudinary(
            token,
            image,
          );
          portraitImageUrls.push(result.secure_url);
        }
      }

      return {
        landscapeImage: landscapeImageUrl,
        portraitImages: portraitImageUrls,
      };
    } catch (error) {
      setSnackbarState({
        open: true,
        message: 'Failed to upload images. Please try again.',
        severity: 'error',
      });
      SentryService.captureError('Error uploading gallery images', error);
      throw error;
    }
  };

  const saveStylist = async () => {
    const galleryData = await saveGalleryImages();

    await updateStylist({
      variables: {
        input: {
          bio: bioData.bio,
          landscapeImage: galleryData.landscapeImage,
          portraitImages: galleryData.portraitImages,
          expertise: selectedExpertise,
          aesthetics: selectedStylingPreference,
          tiktok: bioData.tikTok,
          instagram: bioData.instagram,
          substack: bioData.substack,
        },
      },
      onError: (error) => {
        throw new Error(error.message);
      },
    });
  };

  const onContinuePress = async () => {
    switch (progress) {
      case CREATE_PROFILE_STEP: {
        if (
          profileFormData.maskedPhoneNumber &&
          !isValidPhoneNumber(`+1${profileFormData.maskedPhoneNumber}`, 'US')
        ) {
          setPhoneNumberError(true);
          return;
        }
        if (profileFormData.isCheckBoxChecked === false) {
          setCheckboxError(true);
          return;
        }

        try {
          setIsLoading(true);
          await createNewUser();

          break;
        } catch (error) {
          if (error instanceof ApolloError) {
            setSnackbarState({
              open: true,
              message: shouldSurfaceGraphQLError(error)
                ? error.graphQLErrors?.[0]?.message
                : 'There was a problem creating your profile. Please try again.',
              severity: 'error',
            });
            SentryService.captureError('Error creating user', error);
          }
          return;
        } finally {
          setIsLoading(false);
        }
      }
      case SHOWCASE_STEP: {
        try {
          setIsLoading(true);
          await saveStylist();
          break;
        } catch (error) {
          setSnackbarState({
            open: true,
            message:
              'An error occured while saving your profile. Please try again.',
            severity: 'error',
          });
          SentryService.captureError('Error saving stylist', error);
          setIsLoading(false);
          return;
        } finally {
          setIsLoading(false);
        }
      }
      case PAYMENT_STEP: {
        try {
          setIsLoading(true);
          await updateStylistStripeSetup({
            variables: {
              input: {
                hasCompletedStripeSetup: isStripeAccountSetup,
              },
            },
          });
          break;
        } catch (error) {
          setSnackbarState({
            open: true,
            message:
              'There was a problem saving your payment setup. Please try again.',
            severity: 'error',
          });
          SentryService.captureError('Error saving payment method', error);
          return;
        } finally {
          setIsLoading(false);
        }
      }
      default: {
        break;
      }
    }
    setProgress(progress + 1);
    scrollToTop();
  };

  const renderStepContent = () => {
    switch (progress) {
      case CREATE_PROFILE_STEP: {
        return (
          <CreateProfile
            formData={profileFormData}
            setFormData={setProfileFormData}
            phoneNumberError={phoneNumberError}
            setPhoneNumberError={setPhoneNumberError}
            setCheckboxError={setCheckboxError}
            checkboxError={checkboxError}
            isStylist
          />
        );
      }
      case SOCIALS_STEP: {
        return <Socials bioData={bioData} setBioData={setBioData} />;
      }
      case STYLING_SPECIALTY_STEP: {
        return (
          <SelectExpertise
            selectedItems={selectedExpertise}
            setSelectedItems={setSelectedExpertise}
            enumArray={Object.values(Expertise)}
          />
        );
      }
      case STYLING_PREFERENCES_STEP: {
        return (
          <SelectPreferences
            selectedItems={selectedStylingPreference}
            setSelectedItems={setSelectedStylingPreference}
            enumArray={Object.values(StylingPreference)}
          />
        );
      }
      case SHOWCASE_STEP: {
        return <Showcase images={galleryImages} setImages={setGalleryImages} />;
      }
      case PAYMENT_STEP: {
        return (
          <StylistAddPayment
            isCheckboxChecked={isStripeAccountSetup}
            setIsCheckboxChecked={setIsStripeAccountSetup}
            setIsLoading={setIsLoading}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <>
      {isLastStep ? (
        <StylistOnboardingComplete />
      ) : (
        <ScrollableView
          contentContainerStyle={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
          scrollEnabled
          style={[
            OnboardingContainerStyles,
            { backgroundColor: isLastStep ? green : sand },
          ]}
        >
          <OnboardingHeader
            progress={progress}
            showDarkColors={!isLastStep}
            hideBackButton={isLastStep}
            onPress={() => logOut()}
            authText="Log Out"
            setProgress={setProgress}
          />
          <OnboardingContentContainer isDesktop={isDesktop}>
            <Progress
              titleText={stylistOnboardingTitleTextOptions}
              descriptionContent={stylistOnboardingDescriptionTextOptions}
              progress={progress}
              altStyle={isLastStep}
              isRequired={stylistOnboardingRequiredSteps.includes(progress)}
            />
            <OnboardingBottomContainer isDesktop={isDesktop}>
              <OnboardingFormArea
                isDesktop={isDesktop}
                backgroundColor={isPaymentStep ? white : undefined}
              >
                {renderStepContent()}
              </OnboardingFormArea>
              {progress === STYLING_PREFERENCES_STEP && (
                <MissingStyleSupportText />
              )}
              {IS_WEB && !isLastStep && (
                <OnboardingButtonContainer isDesktop={isDesktop} width={width}>
                  <Button
                    text={isLoading ? '' : 'Continue'}
                    onPress={onContinuePress}
                    disabled={getDisabledState() || isLoading}
                    loading={isLoading}
                  />
                </OnboardingButtonContainer>
              )}
            </OnboardingBottomContainer>
          </OnboardingContentContainer>
        </ScrollableView>
      )}
      {!IS_WEB && !isLastStep && (
        <OnboardingButtonContainer isDesktop={isDesktop} width={width}>
          <Button
            text={isLoading ? '' : 'Continue'}
            onPress={onContinuePress}
            disabled={getDisabledState() || isLoading}
            loading={isLoading}
          />
        </OnboardingButtonContainer>
      )}
    </>
  );
};

export default Onboarding;
